import { useState } from 'react'
import { SourceRepoLink } from '@/shared/components/SourceRepoLink'
import type { VersionListItem } from '@/lib/apiTypes'
import { ServeOverrideControl, serveOverrideToast } from '@/shared/components/ServeOverrideControl'
import type { ServeOverrideAction } from '@/lib/apiTypes'
import { api } from '@/lib/api'
import { formatDate, formatCount } from '@/lib/format'
import { useToast } from '@/context/ToastContext'
import { ModulePolicyModal } from '@/features/modules/components/ModulePolicyModal'
import { VersionConsumableBadge, VersionScanBadge } from '@/features/modules/components/VersionBadges'

// ── Version table row ─────────────────────────────────────────────────────────
// Badges + blocked state render from the list-row payload (useVersions), so there
// is no per-row detail fetch. After a block/unblock, onChanged() refetches the
// list and every badge updates from one request.
export function VersionRow({
  v, isLatest, active, slug, name, provider, canMember, canAdmin,
  rescanBusy, onSelect, onRescan, onUndeprecate, onDeprecate, onChanged,
}: {
  v: VersionListItem
  isLatest: boolean
  active: boolean
  slug: string; name: string; provider: string
  canMember: boolean; canAdmin: boolean
  rescanBusy: boolean
  onSelect: () => void
  onRescan: () => void
  onUndeprecate: () => void
  onDeprecate: () => void
  onChanged: () => void
}) {
  const [policyOpen, setPolicyOpen]   = useState(false)
  const [overrideBusy, setOverrideBusy] = useState(false)
  const { showToast } = useToast()
  // Tri-state serve override, derived from the two legacy force booleans (migration 093):
  // manual block == 'block', consumable override == 'allow', neither == Auto (policy decides).
  const overrideValue: ServeOverrideAction = v.manually_blocked ? 'block' : v.consumable_override ? 'allow' : null

  async function applyOverride(action: ServeOverrideAction, reason?: string) {
    setOverrideBusy(true)
    try {
      await api.put(
        `/v1/modules/${slug}/${name}/${provider}/${encodeURIComponent(v.version)}/serve-override`,
        { action, ...(reason ? { reason } : {}) },
      )
      showToast(serveOverrideToast(`v${v.version}`, action))
      onChanged()
    } catch (e) {
      showToast((e as Error).message, 'error')
    } finally {
      setOverrideBusy(false)
    }
  }

  return (
    <tr
      style={{ cursor: 'pointer', background: active ? 'var(--bg3)' : undefined }}
      onClick={onSelect}
    >
      <td>
        <span style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', color: 'var(--green)' }}>
          v{v.version}
        </span>
        {isLatest && <span className="badge badge-accent" style={{ marginLeft: 8 }}>latest</span>}
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        {v.vcs_repository
          ? <SourceRepoLink
              provider={v.vcs_provider}
              repository={v.vcs_repository}
              gitRef={v.vcs_ref}
              refType={v.vcs_ref_type}
              sha={v.vcs_sha}
              commitUrl={v.vcs_commit_url}
              verb="published"
            />
          : <span className="dim">-</span>}
      </td>
      <td><VersionScanBadge v={v} /></td>
      <td><VersionConsumableBadge v={v} /></td>
      <td style={{ color: 'var(--text2)', fontSize: '0.78rem' }}>
        {v.scan_completed_at ? formatDate(v.scan_completed_at) : <span className="dim">-</span>}
      </td>
      <td style={{ color: 'var(--text2)', fontSize: '0.78rem', fontFamily: 'var(--mono)' }}>
        {v.download_count > 0 ? formatCount(v.download_count) : <span className="dim">-</span>}
      </td>
      <td style={{ color: 'var(--text2)', fontSize: '0.78rem' }}>
        {v.last_downloaded_at ? formatDate(v.last_downloaded_at) : <span className="dim">-</span>}
      </td>
      {(canMember || canAdmin) && (
        <td style={{ display: 'flex', gap: 6 }}>
          {policyOpen && (
            <ModulePolicyModal
              slug={slug} name={name} provider={provider} version={v.version}
              canAdmin={canAdmin} onClose={() => setPolicyOpen(false)}
            />
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={(e) => { e.stopPropagation(); setPolicyOpen(true) }}
          >
            Policies
          </button>
          {canMember && (
            <button
              className="btn btn-ghost btn-sm"
              disabled={rescanBusy}
              onClick={(e) => { e.stopPropagation(); onRescan() }}
            >
              {rescanBusy ? <span className="spinner" /> : 'Rescan'}
            </button>
          )}
          {canAdmin && (
            (v.deprecated_at !== null || v.module_deprecated) ? (
              <button
                className="btn btn-ghost btn-sm"
                style={{ color: 'var(--green)' }}
                onClick={(e) => { e.stopPropagation(); onUndeprecate() }}
              >
                Undeprecate
              </button>
            ) : (
              <button
                className="btn btn-ghost btn-sm"
                style={{ color: 'var(--amber)' }}
                onClick={(e) => { e.stopPropagation(); onDeprecate() }}
              >
                Deprecate
              </button>
            )
          )}
          {canAdmin && (
            <ServeOverrideControl
              value={overrideValue}
              subject={`v${v.version}`}
              busy={overrideBusy}
              compact
              onApply={applyOverride}
            />
          )}
        </td>
      )}
    </tr>
  )
}
